import { useState } from 'react'
import { Plus, Pencil, Trash2 } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import { Switch } from '@/shared/components/ui/switch'
import { Skeleton } from '@/shared/components/ui/skeleton'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/shared/components/ui/table'
import {
  useVariaciones, useCreateVariacion, useUpdateVariacion, useDeleteVariacion,
} from '@/modules/multichannel/hooks/useConfiguracionCampanias'
import { VariacionFormDialog } from './VariacionFormDialog'
import type { VariacionBenchmark } from '@/modules/multichannel/types/configuracion'

export function VariacionesTab() {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<VariacionBenchmark | null>(null)

  const { data: variaciones, isLoading } = useVariaciones()
  const createMutation = useCreateVariacion()
  const updateMutation = useUpdateVariacion()
  const deleteMutation = useDeleteVariacion()

  return (
    <div className="space-y-4">
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              Variaciones de Benchmark
              {variaciones && <Badge className="badge-gradient text-xs">{variaciones.length}</Badge>}
            </CardTitle>
            <Button onClick={() => { setEditing(null); setDialogOpen(true) }} size="sm" className="bg-teal-800 hover:bg-teal-900 text-white">
              <Plus className="mr-2 h-4 w-4" /> Nueva
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
          ) : variaciones && variaciones.length > 0 ? (
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader className="bg-gray-100 dark:bg-[hsl(220,15%,16%)]">
                  <TableRow>
                    <TableHead>Categoria</TableHead>
                    <TableHead>Pesos</TableHead>
                    <TableHead>Calificaciones</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-right"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {variaciones.map(v => (
                    <TableRow key={v.id}>
                      <TableCell className="font-medium">{v.categoria}</TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Badge variant="outline" className="text-xs">E: {v.peso_entrega}%</Badge>
                          <Badge variant="outline" className="text-xs">A: {v.peso_aperturas}%</Badge>
                          <Badge variant="outline" className="text-xs">C: {v.peso_clics}%</Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Badge variant="outline" className="text-xs">A+: {v.calificacion_a_plus_min}</Badge>
                          <Badge variant="outline" className="text-xs">A: {v.calificacion_a_min}</Badge>
                          <Badge variant="outline" className="text-xs">B: {v.calificacion_b_min}</Badge>
                          <Badge variant="outline" className="text-xs">C: {v.calificacion_c_min}</Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Switch checked={v.activo}
                            onCheckedChange={() => updateMutation.mutate({ id: v.id, data: { activo: !v.activo } })}
                            className={v.activo ? 'data-[state=checked]:bg-teal-500' : ''} />
                          <Badge className={v.activo ? 'badge-gradient shadow-none' : 'bg-gray-100 text-gray-500 hover:bg-gray-100 dark:bg-gray-800 dark:text-gray-400 shadow-none'}>
                            {v.activo ? 'Activo' : 'Inactivo'}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => { setEditing(v); setDialogOpen(true) }}><Pencil className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={async () => {
                            if (window.confirm(`Eliminar variacion "${v.categoria}"?`)) await deleteMutation.mutateAsync(v.id)
                          }}><Trash2 className="h-4 w-4 text-gray-600" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">No hay variaciones creadas.</div>
          )}
        </CardContent>
      </Card>

      <VariacionFormDialog
        open={dialogOpen}
        onOpenChange={(open) => { setDialogOpen(open); if (!open) setEditing(null) }}
        variacion={editing}
        onSubmit={async (data) => { await createMutation.mutateAsync(data); setDialogOpen(false) }}
        onUpdate={async (id, data) => { await updateMutation.mutateAsync({ id, data }); setDialogOpen(false) }}
        isSubmitting={createMutation.isPending || updateMutation.isPending}
      />
    </div>
  )
}
